import { Component } from 'react';
import type { ReactNode, ErrorInfo } from 'react';

interface ErrorBoundaryProps {
  children: ReactNode;
}

interface ErrorBoundaryState {
  hasError: boolean;
  error: Error | null;
  /** React's component stack, so a crash names the component that threw. */
  componentStack: string | null;
}

/**
 * Trim a stack to something that fits on screen and still identifies the crash.
 *
 * The error boundary wraps the WHOLE app, so its message alone says nothing
 * about where the failure was -- "Cannot read properties of undefined (reading
 * 'type')" was blamed on the .docx editor twice, in May and again on
 * 2026-09-22, and both fixes missed. Teams desktop locks DevTools and
 * sourcemaps are not shipped, so what the boundary renders is the only
 * evidence that can leave a real client.
 */
export function topFrames(stack: string | null | undefined, n: number): string {
  if (!stack) return '';
  return stack
    .split('\n')
    .map((l) => l.trim())
    .filter(Boolean)
    .slice(0, n)
    .join('\n');
}

/**
 * React Error Boundary — catches render errors in the component tree
 * and shows a fallback UI instead of a blank page.
 *
 * Must be a class component because hooks cannot catch render errors.
 */
export class ErrorBoundary extends Component<ErrorBoundaryProps, ErrorBoundaryState> {
  constructor(props: ErrorBoundaryProps) {
    super(props);
    this.state = { hasError: false, error: null, componentStack: null };
  }

  static getDerivedStateFromError(error: Error): ErrorBoundaryState {
    return { hasError: true, error, componentStack: null };
  }

  componentDidCatch(error: Error, errorInfo: ErrorInfo): void {
    console.error('[ErrorBoundary] Uncaught error:', error);
    console.error('[ErrorBoundary] Component stack:', errorInfo.componentStack);
    this.setState({ componentStack: errorInfo.componentStack ?? null });
    // Future: send to Application Insights or error reporting service
  }

  handleReload = (): void => {
    window.location.reload();
  };

  handleDismiss = (): void => {
    this.setState({ hasError: false, error: null, componentStack: null });
  };

  render(): ReactNode {
    if (this.state.hasError) {
      return (
        <div className="error-boundary">
          <div className="error-boundary__card">
            <h2 className="error-boundary__title">Something went wrong</h2>
            <p className="error-boundary__message">
              An unexpected error occurred. You can try reloading the page or dismissing this error.
            </p>
            {this.state.error && (
              <pre className="error-boundary__details">{this.state.error.message}</pre>
            )}
            {(this.state.componentStack || this.state.error?.stack) && (
              <pre className="error-boundary__trace">
                {[topFrames(this.state.componentStack, 8), topFrames(this.state.error?.stack, 5)]
                  .filter(Boolean)
                  .join('\n--\n')}
              </pre>
            )}
            <div className="error-boundary__actions">
              <button
                className="error-boundary__btn error-boundary__btn--primary"
                onClick={this.handleReload}
              >
                Reload Page
              </button>
              <button
                className="error-boundary__btn error-boundary__btn--secondary"
                onClick={this.handleDismiss}
              >
                Dismiss
              </button>
            </div>
          </div>
        </div>
      );
    }

    return this.props.children;
  }
}
